import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Navigator from "../../component/navigator";
import styles from "./index.module.scss";
import html2canvas from "html2canvas";
import CaptureIcon from "../../icons/capture";
import { IconClose } from "@quark/design";
import ChatItem, { Chat } from "./chat-item";
import ChatInput from "./chat-input";
import { debounce } from "lodash";
import ChatEmpty from "./chat-empty";
import MobileHeader from "./mobile-header";
import { v4 as uuid } from "uuid";
import useOpenId from "./use-openid";
import useDocumentTitle from "../../hooks/use-document-title";

const ChatGPT = () => {
  const resultRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [imgBase64, setImgBase64] = useState("");
  const [windowWidth] = useState(window.innerWidth);
  const isMobile = useMemo(() => windowWidth < 900, [windowWidth]);
  const [sessionId] = useState(uuid());
  const openId = useOpenId();
  useDocumentTitle("AI聊天");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToBottom = useCallback(
    debounce(() => {
      const resultDom = resultRef.current;
      if (resultDom) {
        resultDom.scrollTo(0, resultDom.scrollHeight);
      }
    }, 300),
    []
  );

  const submitText = (inputText: string) => {
    if (loading) return;
    if (!inputText.trim()) return;
    setLoading(true);
    setInput(inputText);
    setResult(" ");
    scrollToBottom();
    const evSrc = new EventSource(
      `//chat.py2fun.com/chatgpt/chat?content=${inputText}&openid=${openId}&session_id=${sessionId}`
    );
    evSrc.addEventListener("message", (ev) => {
      const data = JSON.parse(ev.data);
      setResult(data?.message?.content?.parts[0] || " ");
      scrollToBottom();
    });
    evSrc.addEventListener("open", () => {});
    evSrc.addEventListener("error", (_ev) => {
      evSrc.close();
      setLoading(false);
    });
  };

  const saveImage = () => {
    if (loading) return;
    const resultDom = resultRef.current;
    const captureDom = captureRef.current;
    if (resultDom && captureDom && !capturing) {
      setCapturing(true);
      captureDom.style.display = "none";
      // 将内容缩小到可视区域内才能截图到全部内容
      const scale = resultDom.offsetHeight / resultDom.scrollHeight;
      resultDom.style.transform = `scale(${scale})`;
      resultDom.style.overflow = "unset";
      // 开始截图，使用 windowWidth 减少边缘空白区域
      const options = {
        // allowTaint: true,
        useCORS: true,
        windowWidth: 500,
        // 2 - 15, 保证清晰度
        scale: Math.min(
          15,
          Math.max(2, Math.ceil(resultDom.scrollHeight / 500))
        ),
      };

      html2canvas(resultRef.current, options)
        .then(function (canvas) {
          const imageUrl = canvas.toDataURL();
          setImgBase64(imageUrl);
          // let a = document.createElement("a");
          // a.href = imageUrl;
          // a.download = "ChatGPT.png";
          // a.style.display = "none";
          // document.body.appendChild(a);
          // a.click();
          // a.remove();
        })
        .finally(() => {
          setCapturing(false);
        });
      // 还原内容区域
      resultDom.style.transform = "";
      resultDom.style.overflow = "auto";
      captureDom.style.display = "flex";
    }
  };

  // 获取完成后，新增 chat，并重置 input / result
  useEffect(() => {
    if (!loading && input && result) {
      setChats([
        ...chats,
        {
          key: new Date().getTime(),
          input,
          result,
        },
      ]);
      setInput("");
      setResult("");
    }
  }, [loading, input, result, chats]);

  useEffect(() => {
    if (isMobile) {
      document.body.classList.add("mobile-chatgpt");
    } else {
      document.body.classList.remove("mobile-chatgpt");
    }
  }, [isMobile]);

  const showChats = useMemo(() => {
    return !!chats.length || !!(input && result);
  }, [chats, input, result]);

  // 已完成对话
  const Chats = useMemo(() => {
    return (
      <>
        {chats.map((chat) => (
          <ChatItem dataSource={chat} key={chat.key} />
        ))}
      </>
    );
  }, [chats]);

  // 进行中的对话
  const ProcessingChat = useMemo(() => {
    if (input && result) {
      return <ChatItem dataSource={{ input, result, key: "tmp" }} />;
    }
    return null;
  }, [input, result]);

  return (
    <div className={styles.container}>
      {isMobile ? <MobileHeader /> : <Navigator activeKey="chatgpt" />}
      <div className={styles.content}>
        <div ref={resultRef} className={styles.result}>
          {!showChats && (
            <ChatEmpty onSelect={(text: string) => submitText(text)} />
          )}

          {showChats && (
            <div className={styles.chats}>
              {!loading && (
                <div
                  ref={captureRef}
                  className={styles.capture}
                  onClick={saveImage}
                >
                  截图保存对话 &nbsp;
                  <CaptureIcon />
                </div>
              )}
              {Chats}
              {ProcessingChat}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <ChatInput
            loading={loading}
            onSubmit={(text: string) => submitText(text)}
          />
        </div>
      </div>

      {!!imgBase64 && (
        <div className={styles.imgContainer}>
          <div className={styles.imgBackdrop} />
          <div className={styles.imgClose} onClick={() => setImgBase64("")}>
            <IconClose />
          </div>
          <div className={styles.imgContent}>
            <img src={imgBase64} alt="" />
          </div>
          <div className={styles.imgFooter} onClick={() => setImgBase64("")}>
            <a
              className={styles.imgDownload}
              href={imgBase64}
              download="ChatGPT.png"
            >
              下载
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatGPT;
